import { streamChatSse } from "@/zapi-frontend/shared/api/chatSse";
import { useChatStore, ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { safeJsonParse } from "@/zapi-frontend/utils/safeJsonParse";
import { API_URL } from "@/zapi-frontend/shared/api/axios";
import { useRef, useState } from "react";
import { MAX_CHAT_HISTORY_LENGTH } from "@/zapi-frontend/shared/const/consts";
import { removeContentFromResponse } from "../lib/removeContentFromResponse";
import { getErrorConfig } from "@/zapi-frontend/shared/config/errorMessages";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";

// Not caring too much about this file - chat update will be rewritten and this will be simplified - M

function getLastItems<T>(arr: T[], n: number): T[] {
  return arr.slice(-n);
}

const cleanupMessages = (messages: ChatMessageType[]) => {
  return messages
    .map((message) => {
      if (message.role === "user") return message;
      try {
        const parsedMessage = safeJsonParse(message.content);
        if (parsedMessage.type === "updateChapter") {
          return {
            ...message,
            content: removeContentFromResponse(message.content)
          };
        }
        return message;
      } catch {
        return message;
      }
    })
    .filter((m) => !!m) as ChatMessageType[];
};

type SSEEvent =
  | { type: "start" }
  | { type: "function_call"; message_to_display: string }
  | { type: "function_call_done"; message_to_display: string }
  | { type: "content"; content: string }
  | { type: "finish" };

type StreamCallbacks = {
  onStatusMessage: (message: string) => void;
  onContentChunk: (accumulated: string) => void;
};

async function streamAskResponse(
  requestBody: Record<string, unknown>,
  abortSignal: AbortSignal,
  callbacks: StreamCallbacks
): Promise<string> {
  let accumulated = "";

  await streamChatSse<SSEEvent>({
    url: `${API_URL}/chat/ask`,
    body: requestBody,
    signal: abortSignal,
    onEvent: (event) => {
      switch (event.type) {
        case "function_call":
        case "function_call_done":
          callbacks.onStatusMessage(event.message_to_display);
          break;
        case "content":
          accumulated += event.content;
          callbacks.onContentChunk(accumulated);
          break;
      }
    }
  });

  return accumulated;
}

export function useChatGenerate() {
  const { getChatHistory, addMessage, setLoadingState, resetLoadingState, updatePendingMessage } = useChatStore();
  const { isLabs } = useMeStore();
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isAskStreaming, setIsAskStreaming] = useState(false);
  const [streamingChapterId, setStreamingChapterId] = useState<string | null>(null);
  const askAbortRef = useRef<AbortController | null>(null);

  const startGeneration = async (
    projectId: string,
    chapterId: string,
    content: string,
    model: TModelType | null,
    askOnly = false
  ): Promise<number | null> => {
    const botMessageId = `bot-${Date.now()}`;
    const botLoadingMessage: ChatMessageType = {
      role: "assistant",
      content: "",
      id: botMessageId
    };

    const requestBody = {
      projectId: Number(projectId),
      content: content || "",
      messagesHistory: [
        ...getLastItems(cleanupMessages(getChatHistory(projectId, chapterId)), MAX_CHAT_HISTORY_LENGTH)
      ],
      chapterId: Number(chapterId),
      model: model
    };

    try {
      setIsSendingRequest(true);
      setLoadingState(projectId, chapterId, {
        loadingMessageId: botMessageId,
        pendingBotMessage: botLoadingMessage
      });

      const classifyResponse = await fetch(`${API_URL}/chat/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody)
      });

      if (!classifyResponse.ok) {
        const errorData = await classifyResponse.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(errorData.error || `Request failed with status ${classifyResponse.status}`);
      }

      const data = await classifyResponse.json();

      if (askOnly && data.intent !== "ask") {
        const warningContent = JSON.stringify({
          type: "warning",
          content: "Editing is available per chapter — open the chapter you want to change and ask there."
        });
        addMessage(projectId, chapterId, {
          role: "assistant",
          content: warningContent,
          id: botMessageId
        });
        resetLoadingState(projectId, chapterId);
        return null;
      }

      if (data.intent === "wrong_chapter") {
        let warningMessage: string = data.message;
        if (data.targetChapterId && data.targetChapterName) {
          const chapterUrl = `/projects/${projectId}?tab=document&chapterId=${data.targetChapterId}`;
          warningMessage = `Please use the chat while on the [${data.targetChapterName}](${chapterUrl}) chapter — I can only edit the chapter you're currently viewing.`;
        }
        const warningContent = JSON.stringify({ type: "warning", content: warningMessage });
        addMessage(projectId, chapterId, {
          role: "assistant",
          content: warningContent,
          id: botMessageId
        });
        resetLoadingState(projectId, chapterId);
        return null;
      }

      if (data.intent === "ask") {
        if (data.readOnlyResponse) {
          const answerContent = JSON.stringify({ type: "answer", content: data.readOnlyResponse });
          addMessage(projectId, chapterId, {
            role: "assistant",
            content: answerContent,
            id: botMessageId
          });
          resetLoadingState(projectId, chapterId);
          return null;
        }

        const controller = new AbortController();
        askAbortRef.current = controller;
        setIsAskStreaming(true);
        setStreamingChapterId(chapterId);
        setIsSendingRequest(false);

        let contentStarted = false;
        try {
          const answer = await streamAskResponse(requestBody, controller.signal, {
            onStatusMessage: (statusMsg) => {
              setLoadingState(projectId, chapterId, { progressMessage: statusMsg });
            },
            onContentChunk: (accumulated) => {
              if (!contentStarted) {
                contentStarted = true;
                setLoadingState(projectId, chapterId, { loadingMessageId: null, progressMessage: null });
              }
              const answerContent = JSON.stringify({ type: "answer", content: accumulated });
              updatePendingMessage(projectId, chapterId, answerContent);
            }
          });

          if (answer) {
            const answerContent = JSON.stringify({ type: "answer", content: answer });
            addMessage(projectId, chapterId, {
              role: "assistant",
              content: answerContent,
              id: botMessageId
            });
          }
        } finally {
          askAbortRef.current = null;
          setIsAskStreaming(false);
          setStreamingChapterId(null);
        }

        resetLoadingState(projectId, chapterId);
        return null;
      }

      setLoadingState(projectId, chapterId, {
        pendingGenerationId: data.generationId,
        isGeneratingChapter: false,
        prereqsChaptersToBeGenerated: null,
        isChapterLoaded: false
      });

      return data.generationId;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        resetLoadingState(projectId, chapterId);
        return null;
      }

      console.error("Error starting generation:", error);

      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      const errorConfig = getErrorConfig(errorMessage, !!isLabs);
      reportErrorToSentry(errorMessage, "Chat Island Error", errorConfig.errorKey, {
        projectId,
        chapterId
      });

      const errorContent = JSON.stringify({
        type: "error",
        content: errorConfig.userMessage
      });

      setLoadingState(projectId, chapterId, {
        pendingBotMessage: {
          role: "assistant",
          content: errorContent,
          id: botMessageId
        }
      });

      resetLoadingState(projectId, chapterId);
      return null;
    } finally {
      setIsSendingRequest(false);
    }
  };

  const abortAskStream = () => {
    askAbortRef.current?.abort();
  };

  return {
    isSendingRequest,
    isAskStreaming,
    streamingChapterId,
    startGeneration,
    abortAskStream
  };
}
